import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateSessionInfo } from "../../../../../../store/actions/admin/create-event";
import { EventsState } from "../../../../../../store/types";
import { Session, SessionTypesEnum } from "../../../../../../types/working-model";
import { copyStringToClipboard } from "../../../../../../utils/utils";
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import EditSessionModal from "../../../session/edit-session-modal";
import VideoModal from "../../videos/video-modal";
import GreenroomLogo from "../../../../../../images/greenroom-logo.svg";

import "./session-settings.scss";
import Switch from "../../../../../general-ui/switch/switch";
import { setSessionCss, updateModerationSettings } from "../../../../../../store/actions/admin/create-event/session";
import CustomCSS from "../../../../../general-ui/edit-css/edit-css";

function dateTimeRange(start: number, end: number) {
	const startDate = new Date(start);
	const endDate = new Date(end);

	return `${startDate.toLocaleDateString()}, ${startDate.toLocaleTimeString()}-${endDate.toLocaleTimeString()}`;
}

const SessionSettings: React.FC = () => {
	const {
		CreateSessionReducer: { workingSession },
		AuthReducer: {
			token
		}
	} = useSelector((state: EventsState) => state);
	const [modalOpen, setModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editingCss, setEditingCss] = useState(false);
	const dispatch = useDispatch();
	const editVideo = () => {
		setModalOpen(true);
	};

	const renderSessionType = useMemo(() => {
		if (workingSession) {
			if (workingSession?.session_type === SessionTypesEnum.onDemand) {
				return (
					<div className="settings-section">
						<h6>Session type</h6>
						<div className="settings-type">
							<div className="icon-gradient">
								<Icon name={ICONS.ON_DEMAND} size={12} color={COLORS.BLACK} />
							</div>
							<label>On-demand</label>
						</div>
					</div>
				);
			} else {
				return (
					<>
						<div className="settings-section">
							<h6>Session type</h6>
							<div className="settings-type">
								<div className="icon-gradient">
									<Icon name={ICONS.VIEWERS_EYE} size={12} color={COLORS.BLACK} />
								</div>
								<label>Broadcast</label>
							</div>
						</div>
						<div className="settings-section">
							<h6>Date & Time</h6>
							<p>{dateTimeRange(workingSession.timestamp as number, workingSession.end_timestamp as number)}</p>
						</div>
					</>
				);
			}
		}
	}, [workingSession]);

	const copy = useCallback((str: string) => {
		return () => {
			copyStringToClipboard(str);
		};
	}, []);

	const renderVideoType = useMemo(() => {
		if (workingSession?.session_type === SessionTypesEnum.onDemand) {
			//on demand sessions can have several types or not be defined yet
			if (workingSession.on_demand_video) {
				const video = workingSession.on_demand_video;
				switch (video.type) {
					case "recorded":
					case "uploaded": {
						return (
							<>
								<div className="settings-type" style={{ marginTop: 16 }}>
									<div className="icon-gradient">
										<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
									</div>

									<label>Added video</label>
								</div>

								<div className="video-display">
									{video.image ? (
										<img src={video.image} alt={video.name} />
									) : (
										<div className="video-placeholder" />
									)}
									<div className="video-data">
										<label>{video.name}</label>
										{video.duration && <span className="duration">{new Date(video.duration * 1000).toISOString().substr(11, 8)}</span>}
									</div>
								</div>
							</>
						);
					}
					case "url": {
						return (
							<>
								<div className="settings-type" style={{ marginTop: 16 }}>
									<div className="icon-gradient">
										<Icon name={ICONS.PAPERCLIP} size={12} color={COLORS.BLACK} />
									</div>

									<label>Linked video</label>
								</div>

								<div className="settings-section">
									<h6>Link</h6>
									<div className="video-link">
										<p>{video.source}</p>

										<button onClick={copy(video.source ?? '')}><Icon name={ICONS.COPY} size={12} color={COLORS.CYAN} /></button>
									</div>

								</div>
							</>
						);
					}
					case "embed": {
						return (
							<>
								<div className="settings-type" style={{ marginTop: 16 }}>
									<div className="icon-gradient">
										<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
									</div>

									<label>Embedded iframe</label>
								</div>
								<div className="settings-section">
									<h6>Code</h6>
									<div className="video-link">
										<p>{video.source}</p>

										<button onClick={copy(video.source ?? '')}><Icon name={ICONS.COPY} size={12} color={COLORS.CYAN} /></button>
									</div>
								</div>
							</>
						);
					}
					default: {
						return (
							<>
								<div className="settings-type" style={{ marginTop: 16 }}>
									<div className="icon-gradient">
										<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
									</div>

									<label>Add video</label>
								</div>
							</>
						);
					}
				}
			} else {
				return (
					<>
						<div className="settings-type" style={{ marginTop: 16 }}>
							<div className="icon-gradient">
								<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
							</div>

							<label>Add video</label>
						</div>
					</>
				);
			}
		} else {
			switch (workingSession?.broadcast_type) {
				case "greenroom": {
					return (
						<>
							<div className="settings-type" style={{ marginTop: 16 }}>
								<div className="icon-greenroom">
									<img src={GreenroomLogo} alt="greenroom" />
								</div>

								<label>Greenroom Streaming</label>
							</div>
						</>
					);
				}
				case "rtmps": {
					return (
						<>
							<div className="settings-type" style={{ marginTop: 16 }}>
								<div className="icon-gradient">
									<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
								</div>

								<label>RTMP streaming</label>
							</div>
						</>
					);
				}
			}

			return (
				<>
					<div className="settings-type" style={{ marginTop: 16 }}>
						<div className="icon-gradient">
							<Icon name={ICONS.ADD} size={12} color={COLORS.BLACK} />
						</div>

						<label>Events streaming</label>
					</div>
				</>
			);
		}
	}, [copy, workingSession?.broadcast_type, workingSession?.on_demand_video, workingSession?.session_type]);

	const toggleModeration = useCallback((value: string, on: boolean) => {
		const settings = workingSession?.moderation ?? { chat: false, questions: false, posts: false };

		switch (value) {
			case "chatApprovalRequired": {
				dispatch(updateModerationSettings({ ...settings, chat: on }));
				break;
			}
			case "questionApprovalRequired": {
				dispatch(updateModerationSettings({ ...settings, questions: on }));
				break;
			}
		}
	}, [workingSession, dispatch]);

	const editCustomCss = () => setEditingCss(true);
	const openEdit = useCallback(() => setIsEditing(true), []);
	const closeEdit = useCallback(() => setIsEditing(false), []);
	const closeVideoModal = useCallback(() => setModalOpen(false), []);

	const handleEdit = useCallback((session: Session) => {
		dispatch(updateSessionInfo(session, token!));
		setIsEditing(false);
	}, [dispatch, token]);

	return workingSession && (
		<>
			<div className="settings-block">
				<div className="settings-block-header">
					<h5>Basic info</h5>
					<button onClick={openEdit}><Icon name={ICONS.EDIT} size={16} color={COLORS.CYAN} /></button>
				</div>
				{renderSessionType}
				{workingSession.tracks && workingSession.tracks.length > 0 && (
					<div className="settings-section">
						<h6>Categories</h6>
						<div className="tag-container">
							{workingSession?.tracks?.map((track: string) => (
								<span key={track} className="tag">{track}</span>
							))}
						</div>
					</div>
				)}

				<div className="settings-section">
					<h6>Languages</h6>
					<div className="tag-container">
						{workingSession?.languages.map((lang: string) => (
							<span className="tag" key={lang}>{lang}</span>
						))}
					</div>

				</div>
			</div>
			<div className="settings-block">
				<div className="settings-block-header">
					<h5>Streaming</h5>
					<button onClick={editVideo}><Icon name={ICONS.EDIT} size={16} color={COLORS.CYAN} /></button>
				</div>
				{renderVideoType}
			</div>

			<div className="settings-block">
				<div className="settings-block-header">
					<h5>Moderation</h5>
				</div>

				<div className="field-group switch">
					<label>Chat Messages Require Approval</label>
					<Switch
						value={'chatApprovalRequired'}
						on={workingSession.moderation?.chat}
						onClick={toggleModeration}
					/>
				</div>
				<div className="field-group switch">
					<label>Questions Require Approval</label>
					<Switch
						value={'questionApprovalRequired'}
						on={workingSession.moderation?.questions}
						onClick={toggleModeration}
					/>
				</div>
			</div>

			<div className="settings-block">
				<div className="settings-block-header">
					<h5>Session Custom CSS</h5>
					<button onClick={editCustomCss}><Icon name={ICONS.EDIT} size={16} color={COLORS.CYAN} /></button>
				</div>
				<pre>{workingSession.custom_css}</pre>
			</div>

			<VideoModal
				open={modalOpen}
				onClose={closeVideoModal}
			/>

			<EditSessionModal
				isOpen={isEditing}
				onClose={closeEdit}
				startingSession={workingSession}
				onSave={handleEdit}
			/>

			<CustomCSS
				open={editingCss}
				onClose={() => setEditingCss(false)}
				onChange={(value) => dispatch(setSessionCss(value))}
				value={workingSession.custom_css ?? ""}
			/>
		</>
	);
};

export default SessionSettings;
